import React, {useContext} from "react";
import Grid from "@mui/material/GridLegacy";
import MenuItem from '@mui/material/MenuItem';
import TextField from "@mui/material/TextField";
import Select from '@mui/material/Select';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import InputLabel from '@mui/material/InputLabel';
import {Controller, useForm} from "react-hook-form";
import {CircularProgress, FormControl} from "@mui/material";
import {IPharmacyType} from "../../queries/pharmacy/pharmacyModel";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {ToasterContext} from "../../components/shared/Toaster/ToasterContext";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {otherDetailsValidationSchema} from "../../constants/validationSchemas";
import { pageTitleSx } from "../../constants/designTokens";
import {useMutationUpdatePharmacy} from "../../queries/pharmacy/hooks/useMutationUpdatePharmacy";
import {NotificationType} from "../../constants/global";
import Button from "@mui/material/Button";

interface OtherDetailsProps {
  data: any;
  pharmacyId: number;
  pharmacyTypes: IPharmacyType[];
  isAdminEdit?: boolean;
  refetchPharmacy?: () => void;
}

const OtherDetails:React.FunctionComponent<OtherDetailsProps> = ({
  data,
  pharmacyId,
  pharmacyTypes,
  isAdminEdit,
  refetchPharmacy
}) => {
  const {addToast} = useContext(ToasterContext);
  const { register, handleSubmit, formState: { errors }, control } = useForm({
    resolver: yupResolver(otherDetailsValidationSchema)
  });

  const [updatePharmacy, { loading }] = useMutationUpdatePharmacy();

  const handlePharmacyOtherDetailsSubmit = async (values: any) => {
    const requestData = {
      PharmacyDetail: {
        update: {
          contractManager: { set: values.contractManager },
          managerContactName: { set: values.managerContactName },
          is340B: { set: values.is340B === 'YES' },
          PharmacyDetailTypes: {
            update: {
              data: {
                PharmacyType: {
                  connect: {
                    id: +values.pharmacyType
                  }
                }
              },
              where: { id: data.id }
            }
          }
        }
      },
    }

    updatePharmacy({ variables: { data: requestData, where: { id: pharmacyId } } }).then(() => {
      addToast({ type: NotificationType.Success, title: 'Your changes has been successfully saved'  });
      refetchPharmacy && refetchPharmacy();
    }).catch((error: any) => {
      addToast({ type: NotificationType.Error, title: error.message  });
    });
  }

  return (
    <Box component="form" onSubmit={handleSubmit(handlePharmacyOtherDetailsSubmit)} noValidate>
      { !isAdminEdit && <Typography component="h1" sx={{ ...pageTitleSx, mb: 3 }}>Other Details</Typography> }
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            label="Pharmacy Manager(Pic)/Contact Name"
            id="managerContactName"
            fullWidth
            required
            defaultValue={data.managerContactName}
            helperText={errors.managerContactName?.message?.toString()}
            {...register('managerContactName')}
            error={!!errors.managerContactName}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <TextField
            label="Contract Manager"
            id="contractManager"
            fullWidth
            defaultValue={data.contractManager}
            required
            helperText={errors.contractManager?.message?.toString()}
            {...register('contractManager')}
            error={!!errors.contractManager}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Controller
            name="pharmacyType"
            control={control}
            defaultValue={data.PharmacyDetailTypes[0].PharmacyType.id}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel id="pharmacyType-label">Pharmacy Type</InputLabel>
                <Select
                  {...field}
                  labelId="pharmacyType-label"
                  id="pharmacyType"
                  label="Pharmacy Type"
                  fullWidth
                  required
                  {...register('pharmacyType')}
                  error={!!errors.pharmacyType}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                >
                  {pharmacyTypes.map((pharmacyType: IPharmacyType) => (
                    <MenuItem key={pharmacyType.id} value={pharmacyType.id}>{pharmacyType.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <FormLabel id="is340B-radios">340B Status</FormLabel>
          <Controller
            name="is340B"
            control={control}
            defaultValue={data.is340B ? 'YES' : 'NO'}
            render={({ field }) => (
              <RadioGroup {...field} aria-labelledby="is340B-radios" row id="is340B" >
                <FormControlLabel value="YES" control={<Radio />} label="Yes" />
                <FormControlLabel value="NO" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', pt: 2 }}>
        <Button type="submit" variant='contained' sx={{width: '200px'}}>
          Save
          { loading && <CircularProgress sx={{ml: 2}} size={14} color='inherit' />}
        </Button>
      </Box>
    </Box>
  )
}

export default OtherDetails;